import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { STATUS_COLORS } from "../../lib/constants";
import { ChevronDown, ChevronRight, User as UserIcon, UserCheck } from "lucide-react";

function flatten(tree) {
  const rows = [];
  const walk = (node, depth, isRoot) => {
    rows.push({ node, depth, isRoot });
    if (node.kinder) node.kinder.forEach((k) => walk(k, depth + 1, false));
  };
  walk(tree, 0, true);
  return rows;
}

export default function EmpfehlungsBaum() {
  const [tree, setTree] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    api.get("/me/empfehlungs-baum").then((r) => setTree(r.data));
  }, []);

  if (!tree) return <div className="text-falke-blue">Lade Empfehlungsbaum…</div>;

  // Build hidden set based on collapsed
  const rows = flatten(tree);
  const hidden = new Set();
  for (const { node, depth } of rows) {
    if (collapsed[node.id]) {
      // hide everything beneath this node
      const idx = rows.findIndex((r) => r.node.id === node.id);
      for (let i = idx + 1; i < rows.length && rows[i].depth > depth; i++) {
        hidden.add(rows[i].node.id);
      }
    }
  }

  const toggle = (id) => setCollapsed((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div className="space-y-5" data-testid="empfehlungsbaum-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-falke-blue">Mein Empfehlungsbaum</h1>
        <p className="text-falke-anthracite/70 mt-1">Sieh, wie sich deine Empfehlungen ausbreiten.</p>
      </div>
      <div className="falke-card p-6">
        {rows.filter((r) => !hidden.has(r.node.id)).map(({ node, depth, isRoot }) => {
          const hasKinder = node.kinder && node.kinder.length > 0;
          const isCollapsed = !!collapsed[node.id];
          return (
            <div key={node.id} style={{ marginLeft: depth * 24 }} className={depth > 0 ? "pl-4 border-l-2 border-falke-blue/15" : ""}>
              <div className="flex items-center gap-2 py-2" data-testid={`baum-node-${node.id}`}>
                {hasKinder ? (
                  <button onClick={() => toggle(node.id)} className="text-falke-blue p-0.5 rounded hover:bg-falke-blue/10" data-testid={`baum-toggle-${node.id}`}>
                    {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                  </button>
                ) : (
                  <span className="w-5" />
                )}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isRoot ? "bg-falke-orange text-white" : node.ist_kunde ? "bg-falke-blue text-white" : "bg-falke-blue/10 text-falke-blue"}`}>
                  {node.ist_kunde ? <UserCheck size={16} /> : <UserIcon size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-falke-blue text-sm flex items-center gap-2 flex-wrap">
                    {isRoot ? "Du" : node.name}
                    {isRoot && node.stufe && <span className="text-xs font-semibold text-falke-orange">· {node.stufe}-Stufe</span>}
                    {node.status && <span className={`status-badge text-[10px] !py-0.5 ${STATUS_COLORS[node.status]}`}>{node.status}</span>}
                    {node.ist_kunde && !isRoot && <span className="text-[10px] text-falke-success font-semibold uppercase">Kunde</span>}
                  </div>
                  {node.auftragswert && <div className="text-xs text-falke-anthracite/60 tabular">€ {Number(node.auftragswert).toFixed(0)} netto</div>}
                  {isRoot && <div className="text-xs text-falke-anthracite/60">{node.lifetime} abgeschlossene · {node.kinder.length} aktive Empfehlungen</div>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-xs text-falke-anthracite/60">
        Wenn eine empfohlene Person selbst Kunde wird und weiterempfiehlt, erscheinen ihre Empfehlungen als nächste Ebene.
      </div>
    </div>
  );
}
